import { useState } from 'react';
import { Dokument, DokumentTypConfig, isDocumentPreviewable } from '../../types/bewerber';
import { DocumentPreviewTooltip } from './DocumentPreviewTooltip';
import { DocumentPreviewModal } from './DocumentPreviewModal';

interface DocumentListItemProps {
  document: Dokument;
  bewerberId: string;
  onDownload: (documentId: string, filename: string) => void;
  onDelete: (documentId: string, filename: string) => void;
  isDownloading?: boolean;
  isDeleting?: boolean;
}

export function DocumentListItem({
  document,
  bewerberId,
  onDownload,
  onDelete,
  isDownloading,
  isDeleting,
}: DocumentListItemProps) {
  const [showModal, setShowModal] = useState(false);
  const isPreviewable = isDocumentPreviewable(document.mimeType);

  const handleClick = () => {
    if (isPreviewable) {
      setShowModal(true);
    }
  };

  return (
    <>
      <li className="py-3">
        <div className="flex items-center justify-between">
          <DocumentPreviewTooltip document={document} bewerberId={bewerberId}>
            <button
              onClick={handleClick}
              className={`flex-1 min-w-0 text-left ${
                isPreviewable
                  ? 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded transition-colors'
                  : 'cursor-default'
              }`}
              disabled={!isPreviewable}
              title={isPreviewable ? 'Klicken für Vorschau' : 'Vorschau nicht verfügbar'}
            >
              <div className="flex items-center gap-2">
                {/* Preview indicator icon */}
                {isPreviewable && (
                  <span className="text-primary-500 flex-shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </span>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {document.originalFilename}
                  </p>
                  <p className="text-xs text-gray-500">
                    {DokumentTypConfig[document.typ]?.label || document.typ} •{' '}
                    {(document.fileSize / 1024).toFixed(1)} KB
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(document.uploadedAt).toLocaleDateString('de-DE')}
                  </p>
                </div>
              </div>
            </button>
          </DocumentPreviewTooltip>

          <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
            {/* Download button */}
            <button
              onClick={() => onDownload(document.id, document.originalFilename)}
              disabled={isDownloading}
              className="text-primary-600 hover:text-primary-900 disabled:opacity-50"
              title="Herunterladen"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            </button>

            {/* Delete button */}
            <button
              onClick={() => onDelete(document.id, document.originalFilename)}
              disabled={isDeleting}
              className="text-red-600 hover:text-red-900 disabled:opacity-50"
              title="Löschen"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </li>

      {/* Preview Modal */}
      <DocumentPreviewModal
        document={document}
        bewerberId={bewerberId}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </>
  );
}
